import { useState, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useWallet } from "@/hooks/useWallet";
import { Menu, User, LogOut, Wallet, ChevronDown } from "lucide-react";

const primaryNavLinks = [
  { href: "/browsefirms", label: "Firms" },
  { href: "/compare", label: "Compare" },
  { href: "/offers", label: "Offers" },
  { href: "/journal", label: "Journal" },
];

const moreNavLinks = [
  { href: "/complaints", label: "Complaints" },
  { href: "/cashback", label: "Cashback" },
  { href: "/blog", label: "Blogs" },
];

const Logo = () => (
  <Link to="/" className="flex-shrink-0" aria-label="VerifyProp Home">
    <div className="text-xl sm:text-2xl font-bold tracking-tight">
      <span className="text-electric-blue">Verify</span>
      <span className="text-white">Prop</span>
    </div>
  </Link>
);

const DesktopNavLink = ({ href, children }) => (
  <NavLink
    to={href}
    className="relative px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200"
  >
    {({ isActive }) => (
      <>
        <span className="relative z-10">{children}</span>
        {isActive && (
          <motion.div
            layoutId="activeTab"
            className="absolute inset-0 bg-gray-800/60 rounded-md"
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
          />
        )}
      </>
    )}
  </NavLink>
);

const UserProfileDropdown = ({ user, displayName, walletValue, onSignOut }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button
          variant="ghost"
          aria-label="User profile menu"
          className="relative h-9 w-9 rounded-full bg-gray-800/50 hover:bg-gray-700/60 border border-gray-700/50 transition-colors focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <User className="h-4 w-4 text-gray-300" />
        </Button>
      </motion.div>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      className="w-56 bg-gray-900/95 backdrop-blur-xl border-gray-700/50 text-gray-200 shadow-2xl"
      align="end"
      sideOffset={8}
    >
      <div className="flex flex-col space-y-1 px-3 py-2.5">
        <p className="text-sm font-semibold text-white truncate">{displayName}</p>
        <p className="text-xs text-gray-400 truncate">{user.email}</p>
      </div>
      <DropdownMenuSeparator className="bg-gray-700/50" />
      <DropdownMenuItem className="cursor-default focus:bg-transparent px-3 py-2">
        <Wallet className="mr-2 h-4 w-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-200">{walletValue}</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-gray-700/50" />
      <DropdownMenuItem
        onClick={onSignOut}
        className="cursor-pointer text-red-400 focus:bg-red-400/10 focus:text-red-400 px-3 py-2"
      >
        <LogOut className="mr-2 h-4 w-4" />
        <span>Log out</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const AuthButtons = () => (
  <div className="flex items-center gap-2">
    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
      <Button
        asChild
        variant="ghost"
        className="text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors h-9 px-4"
      >
        <Link to="/auth?mode=login">Sign In</Link>
      </Button>
    </motion.div>
    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
      <Button asChild className="bg-cyan-400 hover:bg-cyan-500 text-black h-9 px-4 font-medium">
        <Link to="/auth?mode=signup">Sign Up</Link>
      </Button>
    </motion.div>
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { formattedValue } = useWallet();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const displayName = user?.user_metadata?.full_name || user?.email || "User";

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-gray-900/95 backdrop-blur-md border-b border-gray-700/50"
          : "bg-gray-900/50 backdrop-blur-sm border-b border-gray-800/30"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {primaryNavLinks.map((link) => (
              <DesktopNavLink key={link.href} href={link.href}>
                {link.label}
              </DesktopNavLink>
            ))}

            {/* More Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setMoreDropdownOpen(true)}
              onMouseLeave={() => setMoreDropdownOpen(false)}
            >
              <button
                aria-label="More navigation options"
                aria-expanded={moreDropdownOpen}
                className="relative px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white transition-colors flex items-center gap-1 outline-none"
              >
                More
                <motion.div animate={{ rotate: moreDropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="h-4 w-4" />
                </motion.div>
              </button>

              <AnimatePresence>
                {moreDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute left-0 top-full mt-2 w-40 bg-gray-900/95 backdrop-blur-xl border border-gray-700/50 rounded-lg shadow-2xl overflow-hidden"
                  >
                    {moreNavLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className="block px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800/60 hover:text-white transition-all duration-150"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Desktop Auth/User Section */}
          <div className="hidden lg:flex items-center">
            {user ? (
              <UserProfileDropdown
                user={user}
                displayName={displayName}
                walletValue={formattedValue}
                onSignOut={handleSignOut}
              />
            ) : (
              <AuthButtons />
            )}
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-300 hover:text-white hover:bg-gray-700/50 h-9 w-9"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-gray-900/98 backdrop-blur-xl border-gray-700/50">
                <div className="flex flex-col h-full">
                  <div className="flex-1 py-6">
                    <nav className="flex flex-col space-y-1">
                      {primaryNavLinks.map((link) => (
                        <NavLink
                          key={link.href}
                          to={link.href}
                          className={({ isActive }) =>
                            `px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                              isActive
                                ? "bg-gray-800/70 text-white"
                                : "text-gray-300 hover:bg-gray-800/50 hover:text-white"
                            }`
                          }
                        >
                          {link.label}
                        </NavLink>
                      ))}

                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mt-2">
                        More
                      </div>

                      {moreNavLinks.map((link) => (
                        <NavLink
                          key={link.href}
                          to={link.href}
                          className={({ isActive }) =>
                            `px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                              isActive
                                ? "bg-gray-800/70 text-white"
                                : "text-gray-300 hover:bg-gray-800/50 hover:text-white"
                            }`
                          }
                        >
                          {link.label}
                        </NavLink>
                      ))}
                    </nav>
                  </div>

                  {/* Mobile User Section */}
                  <div className="border-t border-gray-700/50 pt-4 pb-6">
                    {user ? (
                      <div className="space-y-3">
                        <div className="px-4 py-3 bg-gray-800/50 rounded-lg">
                          <p className="text-sm font-semibold text-white truncate">{displayName}</p>
                          <p className="text-xs text-gray-400 truncate mt-0.5">{user.email}</p>
                        </div>
                        <div className="px-4 py-3 bg-gray-800/50 rounded-lg flex items-center">
                          <Wallet className="mr-2 h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-200">{formattedValue}</span>
                        </div>
                        <Button
                          onClick={handleSignOut}
                          variant="ghost"
                          className="w-full justify-start text-red-400 hover:bg-red-400/10 hover:text-red-400"
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          Log out
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2 px-4">
                        <Button
                          asChild
                          variant="ghost"
                          className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-700/50"
                        >
                          <Link to="/auth?mode=login">Sign In</Link>
                        </Button>
                        <Button asChild className="w-full bg-cyan-400 hover:bg-cyan-500 text-black font-medium">
                          <Link to="/auth?mode=signup">Sign Up</Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
